import React, { useMemo, useState } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import { VAIR_MAX_PLAYERS } from 'constants/vair';
import { EventGroupFormatsEnum, TeamTypesEnum } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import { isPackGroup } from 'utils/shared/time/getEventSchedule';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import ChevronDown from 'svg/ChevronDown';
import ChevronUp from 'svg/ChevronUp';
import OrangeDot from 'svg/Dot';
import FilterIcon from 'svg/FilterIcon';
import Format from 'svg/Format';
import Details from 'svg/RacketTwo';
import Prizes from 'svg/Trophy';
import FilterDropdown from 'components/FilterDropdown';
import classNames from 'styles/utils/classNames';
import { EventProps, EventPropsForForm } from './types';
import {
  ALL_FILTER_VALUE,
  DEFAULT_TIMEZONE,
  type EventGroupFilters,
  buildEventFilterOptions,
  filterEventGroups,
  formatDate,
  formatTime,
  formatWeekday,
  getGroupFormatName,
  getScoringFormatName,
  isShowGroupFormat,
  isShowScoringFormat,
} from './utils';

type EventGroup = EventProps['event']['groups'][0];

type EventTimeSegment = {
  date: string;
  dayName: string;
  startTime: string;
  endTime: string;
};

function getEventTimeDisplay(
  group: EventGroup,
  timeZone?: string,
): { segments: EventTimeSegment[]; isMultiDay: boolean } {
  const segments: EventTimeSegment[] = [];
  const tz = timeZone ?? DEFAULT_TIMEZONE;

  if (Array.isArray(group.timeSlots) && group.timeSlots.length > 0) {
    const validSlots = group.timeSlots.filter((s) => s?.startsAt && s?.endsAt);
    for (const slot of validSlots) {
      const startDate = new Date(slot.startsAt!);
      segments.push({
        date: formatDate({ date: slot.startsAt!, timeZone: tz }),
        dayName: formatWeekday({ date: startDate, timeZone: tz }),
        startTime: formatTime({ date: startDate, timeZone: tz }),
        endTime: formatTime({ date: new Date(slot.endsAt!), timeZone: tz }),
      });
    }
  } else if (group.startsAt && group.endsAt) {
    const startDate = new Date(group.startsAt);
    segments.push({
      date: formatDate({ date: group.startsAt, timeZone: tz }),
      dayName: formatWeekday({ date: startDate, timeZone: tz }),
      startTime: formatTime({ date: startDate, timeZone: tz }),
      endTime: formatTime({ date: new Date(group.endsAt), timeZone: tz }),
    });
  }

  return {
    segments,
    isMultiDay: segments.length > 1,
  };
}

function EventTimeSegmentDisplay({ seg }: { seg: EventTimeSegment }) {
  return (
    <span className="typography-product-body-highlight flex flex-wrap items-center gap-ds-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
      <span>{seg.date}</span>
      <OrangeDot className="text-color-brand h-1 w-1 shrink-0" aria-hidden />
      <span>{seg.dayName}</span>
      <OrangeDot className="text-color-brand h-1 w-1 shrink-0" aria-hidden />
      <span>
        {seg.startTime} - {seg.endTime}
      </span>
    </span>
  );
}

/**
 * Always-visible accordion header showing the event title, fee, date, day, and time range.
 */
function EventCardHeader({
  group,
  timeZone,
  registrationFeeUnitAmount,
}: {
  group: EventGroup;
  timeZone?: string;
  registrationFeeUnitAmount?: number | null;
}) {
  const eventTime = getEventTimeDisplay(group, timeZone);
  // Registration fee is charged once per event, so a $0 division just stays silent.
  const isFree = !group.priceUnitAmount && !registrationFeeUnitAmount;

  return (
    <div className="flex flex-col gap-ds-lg lg:gap-ds-sm">
      {/* Title + fee: stacked on mobile, side-by-side on desktop */}
      <div className="flex w-full flex-col gap-ds-sm md:gap-12 lg:flex-row lg:items-start lg:justify-between">
        <h3 className="typography-product-heading-compact break-all text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {group.title}
        </h3>
        {group.priceUnitAmount ? (
          <div className="flex shrink-0 items-center gap-ds-md">
            <p className="typography-product-body-highlight text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {isPackGroup(group)
                ? pluralize({ count: group.timeSlots?.length ?? 0, singular: 'session' })
                : 'Event Fee'}
            </p>
            <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {convertUnitPriceToFormattedPrice(group.priceUnitAmount, CURRENCY_USD).priceDisplay}
            </p>
          </div>
        ) : isFree ? (
          <div className="flex shrink-0 gap-ds-md">
            <p className="typography-product-element-label rounded-full bg-color-bg-lightmode-primary px-3 py-1 text-color-text-brand dark:bg-color-bg-darkmode-primary">
              Free event
            </p>
          </div>
        ) : null}
      </div>
      {/* Date / day / time - supports multiple time slots like FilledEvent */}
      <div
        className={
          eventTime.isMultiDay
            ? 'flex flex-col flex-wrap gap-ds-sm sm:flex-row'
            : 'flex flex-wrap items-center gap-ds-sm'
        }
      >
        {eventTime.segments.map((seg, i) => (
          <EventTimeSegmentDisplay key={i} seg={seg} />
        ))}
      </div>
    </div>
  );
}

/**
 * Three-column details grid: Format, Play details, and Prizes.
 * Only rendered when the accordion card is expanded.
 */
function EventDetailsGrid({ group, hasPrizes }: { group: EventGroup; hasPrizes?: boolean }) {
  const isVairRoundRobin = group.format === EventGroupFormatsEnum.VairRoundRobin;

  return (
    <div className="flex w-full flex-col justify-between gap-10 md:flex-row md:gap-2">
      {/* Format */}
      <div className="flex w-full flex-col md:w-1/3">
        <div className="flex items-center gap-2">
          <Format className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
          <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Format
          </p>
        </div>
        <ul className="mt-ds-md list-disc space-y-ds-xs pl-5 marker:text-color-brand-primary">
          {!!group.minimumNumberOfGames && !isVairRoundRobin && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Min number of games {group.minimumNumberOfGames}
            </li>
          )}
          {!!group.minimumRating && !!group.maximumRating && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Rating {group.minimumRating} - {group.maximumRating}
            </li>
          )}
          {!!group.minimumRating && !group.maximumRating && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Rating {group.minimumRating} and up
            </li>
          )}
          {!group.minimumRating && !!group.maximumRating && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Rating up to {group.maximumRating}
            </li>
          )}
          {!!group.minimumAge && !!group.maximumAge && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Ages {group.minimumAge} - {group.maximumAge}
            </li>
          )}
          {!!group.minimumAge && !group.maximumAge && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Ages {group.minimumAge} and up
            </li>
          )}
          {!group.minimumAge && !!group.maximumAge && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              All ages up to {group.maximumAge}
            </li>
          )}
          {group.teamLimit && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Max number of{' '}
              {isVairRoundRobin || group.teamType === TeamTypesEnum.Singles ? 'players' : 'teams'}{' '}
              {isVairRoundRobin ? VAIR_MAX_PLAYERS : group.teamLimit}
            </li>
          )}
        </ul>
      </div>

      {/* Play details */}
      <div className="flex w-full flex-col md:w-1/3">
        <div className="flex items-center gap-2">
          <Details className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
          <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Play details
          </p>
        </div>
        <ul className="mt-ds-md list-disc space-y-ds-xs pl-5 marker:text-color-brand-primary">
          {!!group.totalPoints && !!group.winBy && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Games to {group.totalPoints} {isVairRoundRobin ? '' : 'win by'}{' '}
              {isVairRoundRobin ? '' : group.winBy}
            </li>
          )}
          {!!group.totalPoints && !group.winBy && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Games to {group.totalPoints}
            </li>
          )}
          {!!group.winBy && !group.totalPoints && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Win by {group.winBy}
            </li>
          )}
          {!!group.gamesPerMatch && !isVairRoundRobin && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {group.gamesPerMatch} set
            </li>
          )}
          {isShowScoringFormat(group.scoringFormat) && (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {getScoringFormatName(group.scoringFormat)}
            </li>
          )}
        </ul>
      </div>

      {/* Prizes */}
      <div className="flex w-full flex-col md:w-1/3">
        <div className="flex items-center gap-2">
          <Prizes className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
          <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Prizes
          </p>
        </div>
        <ul className="mt-ds-md list-disc space-y-ds-xs pl-5 marker:text-color-brand-primary">
          {hasPrizes ? (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Learn more about prizes in the FAQ section
            </li>
          ) : (
            <li className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              This tournament does not have prizes
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/**
 * Single event accordion card with expand/collapse behavior.
 * Expanded state shows a prominent ring-shadow, collapsed shows a flat card.
 */
function EventCard({
  group,
  hasPrizes,
  isExpanded,
  onToggle,
  timeZone,
  registrationFeeUnitAmount,
  isDetailsHidden = false,
}: {
  group: EventGroup;
  hasPrizes?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  timeZone?: string;
  registrationFeeUnitAmount?: number | null;
  /** SOCIAL events show only the basics (title, time, fee) — no expandable format/details grid */
  isDetailsHidden?: boolean;
}) {
  if (!group) return null;

  return (
    <div
      className={`flex flex-row-reverse gap-8 overflow-hidden rounded-xl bg-color-bg-lightmode-tertiary p-4 dark:bg-color-bg-darkmode-tertiary lg:flex-row lg:p-8 ${
        isExpanded
          ? 'shadow-[0px_0px_0px_2px_white,0px_0px_0px_3px_#b9b9be,0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:shadow-[0px_0px_0px_2px_#18181b,0px_0px_0px_3px_#3f3f46,0px_4px_20px_0px_rgba(0,0,0,0.3)]'
          : ''
      }`}
    >
      {!isDetailsHidden && (
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 self-start"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse event details' : 'Expand event details'}
        >
          {isExpanded ? (
            <ChevronUp className="h-8 w-8 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-6 max-sm:w-6" />
          ) : (
            <ChevronDown className="h-8 w-8 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-6 max-sm:w-6" />
          )}
        </button>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-ds-3xl">
        {/* Header section: title/fee + date/time, and event format when expanded */}
        <div className="flex flex-col gap-ds-2xl">
          <EventCardHeader
            group={group}
            timeZone={timeZone}
            registrationFeeUnitAmount={registrationFeeUnitAmount}
          />
          {isExpanded && !isDetailsHidden && !!isShowGroupFormat(group.format) && (
            <div className="flex flex-col gap-ds-md md:flex-row md:items-center">
              <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Event format
              </p>
              <p className="typography-product-body-highlight text-color-text-brand">
                {getGroupFormatName(group.format)}
              </p>
            </div>
          )}
        </div>

        {/* Details grid: only shown when expanded */}
        {isExpanded && !isDetailsHidden && <EventDetailsGrid group={group} hasPrizes={hasPrizes} />}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

/**
 * Events section with filter bar and accordion cards.
 * Filters by gender, level, type, and age. First card expanded by default.
 */
export default function Events({ event, eventGroups, isReview }: EventPropsForForm) {
  const groups = isReview ? eventGroups || [] : event.groups || [];
  // Only competitive events have a format and details worth expanding.
  const isDetailsHidden = !getEventExperience(event).hasCompetition;

  // Filter state
  const [filters, setFilters] = useState<EventGroupFilters>({
    gender: ALL_FILTER_VALUE,
    type: ALL_FILTER_VALUE,
    level: ALL_FILTER_VALUE,
    age: ALL_FILTER_VALUE,
  });

  const { genderOptions, typeOptions, levelOptions, ageOptions } = useMemo(
    () => buildEventFilterOptions(groups),
    [groups],
  );
  const filteredGroups = useMemo(() => filterEventGroups(groups, filters), [groups, filters]);

  // Mobile filter visibility
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const hasActiveFilter = Object.values(filters).some((v) => v !== ALL_FILTER_VALUE);

  // Accordion state
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const updateFilter = (key: keyof EventGroupFilters) => (value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value as EventGroupFilters[typeof key] }));
    setExpandedIndex(0); // reset accordion when filter changes
  };

  return (
    <div className="flex flex-col gap-ds-xl">
      {/* Filter bar */}
      <div className="flex flex-col gap-4 lg:gap-0">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Events: {filteredGroups.length}
          </h2>

          {/* Desktop: inline filter dropdowns */}
          <div className="hidden flex-wrap items-center gap-ds-xl lg:flex">
            {genderOptions.length > 1 && (
              <FilterDropdown
                label="All genders"
                options={genderOptions}
                selectedValue={filters.gender}
                onValueChange={updateFilter('gender')}
              />
            )}
            {levelOptions.length > 1 && (
              <FilterDropdown
                label="All levels"
                options={levelOptions}
                selectedValue={filters.level}
                onValueChange={updateFilter('level')}
              />
            )}
            {typeOptions.length > 1 && (
              <FilterDropdown
                label="All types"
                options={typeOptions}
                selectedValue={filters.type}
                onValueChange={updateFilter('type')}
              />
            )}
            {ageOptions.length > 1 && (
              <FilterDropdown
                label="All ages"
                options={ageOptions}
                selectedValue={filters.age}
                onValueChange={updateFilter('age')}
              />
            )}
          </div>

          {/* Mobile: filter toggle button */}
          <button
            type="button"
            onClick={() => setShowMobileFilters((prev) => !prev)}
            className={classNames(
              'relative flex h-8 w-8 items-center justify-center rounded-full border transition-colors lg:hidden',
              showMobileFilters
                ? 'border-color-bg-lightmode-invert bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:border-color-bg-darkmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
                : 'border-color-text-lightmode-primary text-color-text-lightmode-primary dark:border-color-text-darkmode-primary dark:text-color-text-darkmode-primary',
            )}
          >
            <FilterIcon className="h-3.5 w-3.5" />
            {hasActiveFilter && !showMobileFilters && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-color-bg-lightmode-primary bg-color-brand-primary dark:border-color-bg-darkmode-primary" />
            )}
          </button>
        </div>

        {/* Mobile: toggled filter dropdowns */}
        {showMobileFilters && (
          <div className="flex flex-wrap items-center gap-ds-xl lg:hidden">
            {genderOptions.length > 1 && (
              <FilterDropdown
                label="All genders"
                options={genderOptions}
                selectedValue={filters.gender}
                onValueChange={updateFilter('gender')}
              />
            )}
            {levelOptions.length > 1 && (
              <FilterDropdown
                label="All levels"
                options={levelOptions}
                selectedValue={filters.level}
                onValueChange={updateFilter('level')}
              />
            )}
            {typeOptions.length > 1 && (
              <FilterDropdown
                label="All types"
                options={typeOptions}
                selectedValue={filters.type}
                onValueChange={updateFilter('type')}
              />
            )}
            {ageOptions.length > 1 && (
              <FilterDropdown
                label="All ages"
                options={ageOptions}
                selectedValue={filters.age}
                onValueChange={updateFilter('age')}
              />
            )}
          </div>
        )}
      </div>

      {/* Event cards */}
      {filteredGroups.map((group, index) => (
        <EventCard
          key={index}
          group={group}
          hasPrizes={event.hasPrizes}
          isExpanded={!isDetailsHidden && expandedIndex === index}
          onToggle={() => handleToggle(index)}
          timeZone={resolveVenueTimeZone(event) || undefined}
          registrationFeeUnitAmount={event.registrationPriceUnitAmount}
          isDetailsHidden={isDetailsHidden}
        />
      ))}

      {filteredGroups.length === 0 && groups.length > 0 && (
        <p className="typography-product-body-highlight py-ds-xl text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          No events match the selected filters
        </p>
      )}
    </div>
  );
}
